// The jotai atoms orchestrate that window location search params are parsed and synced into the jotai store. This state is used on the ticket overview page (React Table and filter panel)
// When you interact with the ticket table or filters, the state is synced back into the window location.
// The state is mapped to query params for the GET /ticket call.

import type { ZodType } from 'zod';
import { z } from 'zod';
import type { TicketFilter, TicketListDto } from '#pie/internal/api/pie-bff';
import {
  TicketOrigin,
  TicketStatus,
  TicketView,
} from '#pie/internal/api/pie-bff';
import { getValues } from '#pie/utils/ZodUtils';
import { createFiltersAtom } from './createFiltersAtom';
import { createSortingAtom } from './createSortingAtom';
import type { DefaultSorting } from './defaultSorting';
import { createPaginationAtom } from './pagination';

// Schemas to validate the search params
const ticketStatuses = Object.values(TicketStatus) as [
  TicketStatus,
  ...TicketStatus[],
];
const ticketOrigins = Object.values(TicketOrigin) as [
  TicketOrigin,
  ...TicketOrigin[],
];

export const defaultTicketFilters = {
  ticketView: TicketView.InProgress,
};

export const ticketFiltersSchema: ZodType<TicketFilter, unknown> = z
  .object({
    ean18: z.string().optional(),
    handlerIds: z.array(z.string()).optional(),
    issuerIds: z.array(z.string()).optional(),
    mainCategoryIds: z.array(z.string()).optional(),
    origins: z.array(z.enum(ticketOrigins)).optional(),
    regionalGridOperatorIds: z.array(z.string()).optional(),
    statuses: z.array(z.enum(ticketStatuses)).optional(),
    subCategoryIds: z.array(z.string()).optional(),
    teamIds: z.array(z.string().nullable()).optional(),
    ticketView: z.enum(getValues(TicketView)),
  })
  .catch(defaultTicketFilters);

export const ticketPaginationAtom = createPaginationAtom([10, 25, 50]);

export const ticketFiltersAtom = createFiltersAtom<TicketFilter>({
  defaultFilters: defaultTicketFilters,
  filtersSchema: ticketFiltersSchema,
  paginationAtom: ticketPaginationAtom,
});

export const defaultTicketSorting: DefaultSorting<TicketListDto> = {
  desc: true,
  id: 'createdOn',
};

export const ticketSortingAtom = createSortingAtom({
  defaultSorting: defaultTicketSorting,
});
